'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  PHONE_COUNTRIES,
  PREFERRED_PHONE_COUNTRIES,
  countryName,
  dialCodeFor,
  flagEmoji,
  phoneCountry,
  splitE164,
  splitInternational,
} from '@/lib/phone/countries';
import { getLoadedPhoneLib, loadPhoneLib, type PhoneLib } from '@/lib/phone/phone-lib';
import { resolveWhatsapp } from '@/lib/phone/whatsapp-input';
import { trackPostHogEvent } from '@/lib/analytics/posthog';

// Todos los países con indicativo (245). Antes eran 18 fijos y clientes de Países Bajos,
// Guatemala, Polonia, Uruguay, Suiza, Israel u Honduras no podían reservar en la web
// (auditoría SEO 14-oct-2026). El selector es nativo: en móvil abre la lista del sistema y
// no queda tapado ni superpuesto a los demás campos del modal.

const MANUAL_OPTION = '__manual__';

export interface PhoneInputProps {
  locale?: 'es' | 'en';
  id: string;
  label: string;
  value: string; // Número completo con prefijo (`+573001234567`) o vacío
  onChange: (fullNumber: string) => void; // Devuelve E.164 «+573001234567» (o '' si no hay número)
  onBlur?: () => void;
  error?: string;
  defaultCountry?: string; // ISO code, default 'CO'
}

export default function PhoneInput({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  defaultCountry = 'CO',
  locale = 'es',
}: PhoneInputProps) {
  const [country, setCountry] = useState(() => phoneCountry(defaultCountry)?.iso ?? 'CO');
  const [localNumber, setLocalNumber] = useState('');
  const [lib, setLib] = useState<PhoneLib | null>(() => getLoadedPhoneLib());
  const countryRef = useRef(country);
  const localRef = useRef(localNumber);
  const inputRef = useRef<HTMLInputElement>(null);
  countryRef.current = country;
  localRef.current = localNumber;

  const emit = (iso: string, text: string, libNow: PhoneLib | null = lib) => {
    onChange(resolveWhatsapp(text, iso, libNow).value);
  };

  // La librería de validación se pide al abrir el formulario, no con la página.
  useEffect(() => {
    let alive = true;
    if (!lib) {
      void loadPhoneLib().then((mod) => {
        if (alive && mod) setLib(mod);
      });
    }
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cuando llega la librería, reinterpretar lo ya escrito (0 nacional, indicativo repetido).
  useEffect(() => {
    if (lib && localRef.current) emit(countryRef.current, localRef.current, lib);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lib]);

  // Sincronizar value externo (si viene preseteado con prefijo)
  useEffect(() => {
    if (!value) return;
    const split = splitE164(value);
    if (!split) {
      setLocalNumber(value);
      return;
    }
    const current = phoneCountry(countryRef.current);
    const iso =
      current && current.dial === split.dial
        ? current.iso
        : PHONE_COUNTRIES.find((c) => c.dial === split.dial && c.main)?.iso ?? countryRef.current;
    setCountry(iso);
    setLocalNumber(split.national);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options = useMemo(() => {
    const preferred = PREFERRED_PHONE_COUNTRIES.map((iso) => phoneCountry(iso)).filter(
      (c): c is NonNullable<typeof c> => Boolean(c),
    );
    const preferredSet = new Set<string>(PREFERRED_PHONE_COUNTRIES);
    const rest = PHONE_COUNTRIES.filter((c) => !preferredSet.has(c.iso)).sort((a, b) =>
      countryName(a.iso, locale).localeCompare(countryName(b.iso, locale), locale),
    );
    return { preferred, rest };
  }, [locale]);

  const changeCountry = (iso: string, method: 'select' | 'international_input') => {
    if (iso === countryRef.current) return;
    trackPostHogEvent('checkout_phone_country_changed', {
      field_id: id,
      from_country: countryRef.current,
      to_country: iso,
      method,
    });
    countryRef.current = iso;
    setCountry(iso);
  };

  const handleSelectCountry = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const iso = e.target.value;
    if (iso === MANUAL_OPTION) {
      // Primera opción (idea del founder, 14-oct-2026): escribir el indicativo a mano. El campo
      // queda con «+» y el país se fija solo al completar el código (splitInternational).
      trackPostHogEvent('checkout_phone_country_changed', {
        field_id: id,
        from_country: countryRef.current,
        to_country: null,
        method: 'manual_option',
      });
      const digits = localRef.current.replace(/\D/g, '');
      const text = `+${digits}`;
      setLocalNumber(text);
      onChange('');
      window.setTimeout(() => {
        const input = inputRef.current;
        if (!input) return;
        input.focus();
        input.setSelectionRange(text.length, text.length);
      }, 0);
      return;
    }
    changeCountry(iso, 'select');
    emit(iso, localRef.current);
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Se conservan «+» y separadores: un número pegado o autocompletado con «+31 6…» o
    // «0031…» fija su país en lugar de pegarse detrás del indicativo elegido.
    const raw = e.target.value.replace(/[^\d+\s\-().]/g, '');
    const intl = splitInternational(raw, countryRef.current);
    if (intl) {
      const national = intl.national.replace(/\D+/g, ' ').trim();
      changeCountry(intl.iso, 'international_input');
      setLocalNumber(national);
      emit(intl.iso, national);
      return;
    }
    // Sin espacios iniciales: tras «+31» el siguiente separador tecleado no debe quedar delante.
    const text = raw.replace(/^\s+/, '');
    setLocalNumber(text);
    if (/^(\+|00)/.test(text)) {
      // Indicativo incompleto («+3»): todavía no hay un número que enviar.
      onChange('');
      return;
    }
    emit(countryRef.current, raw);
  };

  const dial = dialCodeFor(country);
  const manualPending = /^(\+|00)/.test(localNumber);
  const placeholder = country === 'CO' ? '3001234567' : locale === 'en' ? 'Phone number' : 'Número';
  const optionLabel = (iso: string, d: string) => `${flagEmoji(iso)} ${countryName(iso, locale)} +${d}`;

  return (
    <div>
      <label htmlFor={id} className="block text-xs text-gray-600 mb-1">
        {label}
      </label>
      <div
        className={`flex w-full border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-offset-1 transition-colors ${
          error
            ? 'border-red-400 focus-within:ring-red-200 bg-red-50'
            : 'border-gray-200 focus-within:ring-gray-900/10 focus-within:border-gray-900'
        }`}
      >
        {/* Selector país: select nativo transparente sobre la bandera + indicativo */}
        <div className="relative flex items-center gap-1.5 px-2.5 py-2.5 bg-gray-50 border-r border-gray-200 hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700 flex-shrink-0">
          <span className="text-base leading-none" aria-hidden="true">
            {manualPending ? '🌐' : flagEmoji(country)}
          </span>
          <span className="text-xs text-gray-600" aria-hidden="true">
            {manualPending ? '+…' : `+${dial}`}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-gray-500" aria-hidden="true" />
          <select
            value={country}
            onChange={handleSelectCountry}
            aria-label={locale === 'en' ? 'Select country' : 'Seleccionar país'}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          >
            <option value={MANUAL_OPTION}>
              {locale === 'en' ? '✍️ Type country code (+…)' : '✍️ Escribir indicativo (+…)'}
            </option>
            {options.preferred.map((c) => (
              <option key={`p-${c.iso}`} value={c.iso}>
                {optionLabel(c.iso, c.dial)}
              </option>
            ))}
            <option disabled value="">
              ──────────
            </option>
            {options.rest.map((c) => (
              <option key={c.iso} value={c.iso}>
                {optionLabel(c.iso, c.dial)}
              </option>
            ))}
          </select>
        </div>

        {/* Input número: admite «+indicativo» y detecta el país */}
        <input
          ref={inputRef}
          id={id}
          type="tel"
          name="phone"
          autoComplete="tel"
          inputMode="tel"
          value={localNumber}
          onChange={handleNumberChange}
          onBlur={onBlur}
          placeholder={placeholder}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          className="flex-1 px-3 py-2.5 text-sm bg-transparent focus:outline-none min-w-0"
        />
      </div>
      {error && (
        <p id={`${id}-error`} className="text-xs text-red-600 mt-1">
          {error}
        </p>
      )}
    </div>
  );
}
